import { OrgLite } from 'types/org';
import { UserLite } from 'types/user';
import { CustomPropertiesType, DateString, DateTimeString } from 'types';
import {
  OrgSupplier,
  OrgSupplierConnection,
  OrgSupplierLite,
} from 'types/suppliers';
import { ProductionRunLite } from 'types/production';
import { Tag } from 'types/clientV2/tag';
import { Lite, PATCHSchema, POSTSchema } from 'types/api';
import { PurchaseRequestSchemaLite } from 'types/clientV2/purchaseRequests';
import { TeamLite } from 'types/properties';
import { OrgPart } from 'types/part';
import { StockLocationSchemaLite } from 'types/clientV2/stockLocations';
import { DocumentTemplate, PackagingOptions } from 'types/inventory';
import { OutboundEmailTemplate } from 'types/email';
import { ChipColor } from 'ui-component/extended/Chip';
import {
  OrgPartSchema,
  PART_VERSION,
  V2OrgPartQueryAndClientV2Types,
} from 'types/clientV2/parts';
import { StockDocumentSchema } from 'types/clientV2/stockDocuments';
import {
  DimensionClassOptions,
  UnitOfMeasureOptions,
} from 'types/unitsOfMeasure';

export enum RfqStatus {
  CLOSED = 'closed',
  APPROVED = 'approved',
  VOIDED = 'voided',
  SENT = 'sent',
  DRAFT = 'draft',
}

export const rfqStatusLabels = {
  [RfqStatus.CLOSED]: 'Closed',
  [RfqStatus.APPROVED]: 'Approved',
  [RfqStatus.VOIDED]: 'Rejected',
  [RfqStatus.SENT]: 'Sent',
  [RfqStatus.DRAFT]: 'Draft',
};

export const rfqStatusColorMapper: { [key: string]: ChipColor } = {
  Closed: ChipColor.warning,
  Approved: ChipColor.success,
  Rejected: ChipColor.error,
  Sent: ChipColor.success,
  Draft: ChipColor.grey,
};

export type RfqSchema = {
  id: string;
  rfqCode: string;
  internalNotes: string;
  externalNotes: string;
  notesToSuppliers: string;
  archivedAt: DateTimeString | null;
  approvedAt: DateTimeString | null;
  voidedAt: DateTimeString | null;
  closedAt: DateTimeString | null;
  org: OrgLite;
  processingOrg: OrgLite;
  createdBy: UserLite | null;
  recipients: string[];
  archivedBy: UserLite | null;
  approvedBy: UserLite | null;
  voidedBy: UserLite | null;
  closedBy: UserLite | null;
  destinationLocation: StockLocationSchemaLite | null;
  customProperties: CustomPropertiesType;
  extDataSourceId: string | null;
  dataSource: string | null;
  dataSourceSyncedAt: DateTimeString | null;
  externallySynced: boolean;
  tags: Tag[];
  responses: number;
  responseProgress: string;
  status: RfqStatus;
  archived: boolean;
  createdAt: DateTimeString;
  updatedAt: DateTimeString;
  team: TeamLite | null;
  sentAt: DateTimeString | null;
};

export type RfqLiteSchema = Lite<RfqSchema>;

type IntegrationFields = {
  syncedFromIntegration?: boolean;
  integrationSourceName?: string | null;
  integrationRecordId?: string | null;
  syncedFromIntegrationAt?: DateTimeString;
};

export type RfqPOSTSchema = POSTSchema<
  RfqSchema & IntegrationFields,
  | 'syncedFromIntegration'
  | 'integrationSourceName'
  | 'integrationRecordId'
  | 'syncedFromIntegrationAt'
  | 'tags'
  | 'team'
  | 'customProperties'
  | 'internalNotes'
  | 'externalNotes'
  | 'notesToSuppliers'
  | 'destinationLocation'
  | 'org'
  | 'processingOrg'
>;

export type RfqPATCHSchema = PATCHSchema<RfqPOSTSchema>;

export type RfqLineSchema = {
  id: string;
  org: OrgLite;
  part: string | null;
  description: string | null;
  quant: number;
  dimensionValue: number;
  dimensionUnit: UnitOfMeasureOptions;
  dimensionClass: DimensionClassOptions;
  targetUnitPrice: number;
  internalNotes: string | null;
  externalNotes: string | null;
  toSuppliersNotes: string | null;
  targetDeliveryDate: DateString | null;
  rfq: RfqLiteSchema;
  productionRun: ProductionRunLite | null;
  purchaseRequest: PurchaseRequestSchemaLite | null;
  tags: Tag[];
  customProperties: CustomPropertiesType;
  extDataSourceId: string | null;
  dataSource: string | null;
  dataSourceSyncedAt: DateTimeString | null;
  externallySynced: boolean;
  team: TeamLite | null;
  // When an org has been converted to use Parts V2, the type will be OrgPartSchema (V2 Part)
  // Otherwise, it will be old OrgPart schema
  orgPart: OrgPart | OrgPartSchema | null;
  createdAt: DateTimeString;
  updatedAt: DateTimeString;
  partVersion?: PART_VERSION;
} & Pick<V2OrgPartQueryAndClientV2Types, 'v2OrgPart'>;

export type RfqLineLiteSchema = Lite<RfqLineSchema>;

export type RfqLinePOSTSchema = POSTSchema<
  RfqLineSchema & IntegrationFields,
  'quant' | 'rfq',
  | 'syncedFromIntegration'
  | 'integrationSourceName'
  | 'integrationRecordId'
  | 'syncedFromIntegrationAt'
  | 'tags'
  | 'team'
  | 'customProperties'
  | 'part'
  | 'description'
  | 'internalNotes'
  | 'externalNotes'
  | 'toSuppliersNotes'
  | 'targetDeliveryDate'
  | 'productionRun'
  | 'targetUnitPrice'
  | 'purchaseRequest'
  | 'v2OrgPart'
>;

export type RfqLinePATCHSchema = PATCHSchema<RfqLinePOSTSchema>;

export type RfqSupplierJoinSchema = {
  id: string;
  rfq: RfqLiteSchema;
  orgSupplier: OrgSupplierLite;
  createdAt: DateTimeString;
  sentAt: DateTimeString | null;
  createdBy: UserLite | null;
  sentBy: UserLite | null;
};

export type RfqSupplierJoinLiteSchema = Lite<RfqSupplierJoinSchema>;

export type RfqSupplierJoinPOSTSchema = POSTSchema<
  RfqSupplierJoinSchema,
  never,
  'rfq' | 'orgSupplier'
>;

export type RfqSupplierJoinPATCHSchema = PATCHSchema<RfqSupplierJoinPOSTSchema>;

export type RfqSupplierJoinSendRfqSchema = {
  ids: RfqSupplierJoinSchema['id'][];
  outboundEmailTemplate?: OutboundEmailTemplate['id'] | null;
  documentTemplate?: DocumentTemplate['id'] | null;
  attachments?: StockDocumentSchema['id'][];
};

export enum SupplierQuoteStatus {
  RECEIVED = 'received',
  APPROVED = 'approved',
  VOIDED = 'voided',
  EXPIRED = 'expired',
}

export const supplierQuoteStatusLabels = {
  [SupplierQuoteStatus.RECEIVED]: 'Received',
  [SupplierQuoteStatus.APPROVED]: 'Approved',
  [SupplierQuoteStatus.VOIDED]: 'Rejected',
  [SupplierQuoteStatus.EXPIRED]: 'Expired',
};

export const supplierQuoteStatusColorMapper: { [key: string]: ChipColor } = {
  Received: ChipColor.secondary,
  Approved: ChipColor.success,
  Rejected: ChipColor.error,
  Expired: ChipColor.warning,
};

export type SupplierQuoteSchema = {
  id: string;
  quoteNumber: string | null;
  internalNotes: string | null;
  externalNotes: string | null;
  supplierNotes: string | null;
  connectionType: OrgSupplierConnection;
  createdAt: DateTimeString;
  archivedAt: DateTimeString | null;
  receivedAt: DateTimeString | null;
  expiresAt: DateTimeString | null;
  approvedAt: DateTimeString | null;
  voidedAt: DateTimeString | null;
  receivedBy: UserLite | null;
  archivedBy: UserLite | null;
  approvedBy: UserLite | null;
  voidedBy: UserLite | null;
  org: OrgLite;
  processingOrg: OrgLite;
  orgSupplier: OrgSupplierLite;
  destinationLocation: StockLocationSchemaLite | null;
  rfq: RfqLiteSchema | null;
  tags: Tag[];
  customProperties: CustomPropertiesType;
  extDataSourceId: string | null;
  dataSource: string | null;
  dataSourceSyncedAt: DateTimeString | null;
  externallySynced: boolean;
  status: SupplierQuoteStatus;
  archived: boolean;
  team: TeamLite | null;
};

export type SupplierQuoteSubschema = Omit<
  Lite<
    SupplierQuoteSchema,
    'destinationLocation' | 'rfq' | 'tags' | 'customProperties'
  >,
  'orgSupplier'
> & {
  orgSupplier: Pick<OrgSupplier, 'name' | 'id'>;
};

export type SupplierQuoteLiteSchema = Lite<SupplierQuoteSchema>;

export type SupplierQuotePOSTSchema = POSTSchema<
  SupplierQuoteSchema & IntegrationFields,
  'orgSupplier',
  | 'syncedFromIntegration'
  | 'integrationSourceName'
  | 'integrationRecordId'
  | 'syncedFromIntegrationAt'
  | 'tags'
  | 'team'
  | 'customProperties'
  | 'internalNotes'
  | 'externalNotes'
  | 'supplierNotes'
  | 'quoteNumber'
  | 'destinationLocation'
  | 'rfq'
  | 'expiresAt'
  | 'connectionType'
>;

export type SupplierQuotePATCHSchema = PATCHSchema<SupplierQuotePOSTSchema>;

export type Price = {
  to: number;
  from: number;
  unitPrice: number;
  currency: string;
};

export type SupplierQuoteLineSchema = {
  id: string;
  part: string | null;
  description: string | null;
  moq: number | null;
  multiple: number | null;
  maximum: number | null;
  prices: Price[];
  shipsInLead: number | null;
  noBid: boolean;
  singleUse: boolean;
  coa: string | null;
  notes: string | null;
  createdAt: DateTimeString;
  approvedAt: DateTimeString | null;
  rejectedAt: DateTimeString | null;
  voidedAt: DateTimeString | null;
  isAuthorized: boolean;
  packaging: PackagingOptions;
  sku: string | null;
  approvedBy: UserLite | null;
  rejectedBy: UserLite | null;
  voidedBy: UserLite | null;
  rfqLine: RfqLineLiteSchema | null;
  supplierQuote: SupplierQuoteLiteSchema;
  tags: Tag[];
  customProperties: CustomPropertiesType;
  extDataSourceId: string | null;
  dataSource: string | null;
  dataSourceSyncedAt: DateTimeString | null;
  externallySynced: boolean;
  // When an org has been converted to use Parts V2, the type will be OrgPartSchema (V2 Part)
  // Otherwise, it will be old OrgPart schema
  orgPart: OrgPart | OrgPartSchema | null;
  team: TeamLite | null;
  link: string | null;
  lastScrapedAt: DateTimeString | null;
  duties: string | null;
  salesTax: string | null;
  shipping: string | null;
  otherTotal: string | null;
  dimensionUnit: UnitOfMeasureOptions | null;
  dimensionValue: string | number | null;
  dimensionClass: DimensionClassOptions;
  bulkQuant: string | number | null;
} & Pick<V2OrgPartQueryAndClientV2Types, 'v2OrgPart'>;

export type SupplierQuoteLineSchemaLite = Lite<SupplierQuoteLineSchema>;

export type SupplierQuoteLineWithQuoteSchema = Lite<
  SupplierQuoteLineSchema,
  'supplierQuote'
>;
export type SupplierQuoteLineSubschema = Lite<
  SupplierQuoteLineSchema,
  | 'tags'
  | 'team'
  | 'orgPart'
  | 'rfqLine'
  | 'prices'
  | 'v2OrgPart'
  | 'approvedBy'
  | 'voidedBy'
>;

export type SupplierQuoteLinePOSTSchema = POSTSchema<
  SupplierQuoteLineSchema & IntegrationFields,
  'supplierQuote',
  | 'syncedFromIntegration'
  | 'integrationSourceName'
  | 'integrationRecordId'
  | 'syncedFromIntegrationAt'
  | 'tags'
  | 'team'
  | 'customProperties'
  | 'part'
  | 'description'
  | 'moq'
  | 'multiple'
  | 'maximum'
  | 'shipsInLead'
  | 'coa'
  | 'notes'
  | 'sku'
  | 'rfqLine'
  | 'packaging'
  | 'v2OrgPart'
  | 'isAuthorized'
  | 'noBid'
  | 'singleUse'
  | 'prices'
  | 'link'
  | 'lastScrapedAt'
  | 'duties'
  | 'salesTax'
  | 'shipping'
  | 'otherTotal'
  | 'dimensionValue'
  | 'dimensionUnit'
  | 'dimensionClass'
  | 'bulkQuant',
  'prices'
>;

export type SupplierQuoteLinePATCHSchema =
  PATCHSchema<SupplierQuoteLinePOSTSchema>;
